import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Calendar, Clock, Check, Copy, Phone, Star, BadgeCheck } from "lucide-react";
import "./transport.css";
import { BookingHeader, PrimaryBtn, PriceRow, PaymentMethods, SecureNote } from "./components";
import { TAXI_PLACES, fmt, makeRef } from "./transportData";
import { saveTicket } from "./ticketStore";

const SERVICES = [
  { id: "trajet", name: "Trajet premium", desc: "Un déplacement, chauffeur dédié", base: 15000 },
  { id: "demi", name: "Mise à disposition · Demi-journée", desc: "4 heures, kilométrage illimité en ville", base: 45000 },
  { id: "journee", name: "Mise à disposition · Journée", desc: "8 heures, chauffeur à votre service", base: 80000 },
];
const VEHICLES = [
  { id: "berline", name: "Berline", desc: "Toyota Camry ou similaire", mult: 1 },
  { id: "suv", name: "SUV Premium", desc: "Land Cruiser Prado ou similaire", mult: 1.3 },
  { id: "van", name: "Van VIP", desc: "Jusqu'à 6 passagers", mult: 1.6 },
];
const HOURS = ["07:00", "08:00", "09:00", "10:00", "12:00", "14:00", "16:00", "18:00"];
const CHAUFFEUR = { name: "Konan A.", rating: "4,9", rides: 342, car: "Mercedes Classe E", plate: "AB 5678 CI" };
const STEPS = ["service", "details", "payment", "done"];
const TITLES = { service: "Chauffeur privé", details: "Détails de la réservation", payment: "Paiement", done: "Réservation confirmée" };

export default function ChauffeurPrivePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("service");
  const [service, setService] = useState("trajet");
  const [vehicle, setVehicle] = useState("berline");
  const [pickup, setPickup] = useState(TAXI_PLACES[0]);
  const [date, setDate] = useState("2026-09-28");
  const [hour, setHour] = useState("08:00");
  const [notes, setNotes] = useState("");
  const [payMethod, setPayMethod] = useState("orange");
  const [paying, setPaying] = useState(false);
  const [bookingRef, setBookingRef] = useState(null);

  const svc = SERVICES.find((s) => s.id === service);
  const veh = VEHICLES.find((v) => v.id === vehicle);
  const total = Math.round(svc.base * veh.mult / 500) * 500;
  const dateLabel = new Date(date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

  const goBack = () => {
    const i = STEPS.indexOf(step);
    if (step === "done" || i === 0) return navigate("/search");
    setStep(STEPS[i - 1]);
  };
  const pay = () => {
    setPaying(true);
    setTimeout(() => {
      setPaying(false);
      const ref = makeRef("AYA-VIP", pickup.commune.slice(0, 3).toUpperCase(), veh.id.slice(0, 3).toUpperCase());
      setBookingRef(ref);
      saveTicket({
        type: "chauffeur", ref, title: `Chauffeur privé · ${veh.name}`,
        sub: `${svc.name} · ${dateLabel} · ${hour}`, price: total,
        details: { Formule: svc.name, Véhicule: `${veh.name} (${CHAUFFEUR.car})`, "Prise en charge": pickup.name, "Date · Heure": `${dateLabel} · ${hour}`, Chauffeur: CHAUFFEUR.name },
      });
      setStep("done");
    }, 1200);
  };

  return (
    <div className="tp-page" data-testid="chauffeur-page">
      <BookingHeader title={TITLES[step]} onBack={goBack} />
      <div className="tp-container">

        {step === "service" && (
          <>
            <img src="/aya/transport/tp_chauffeur.jpg" alt="Chauffeur privé AYA" className="tp-hero" />
            <div className="tp-sechead"><div><h2>Choisissez votre formule</h2><p>Chauffeurs professionnels vérifiés · Véhicules premium</p></div></div>
            {SERVICES.map((s) => (
              <div key={s.id} className={`tp-payrow ${service === s.id ? "on" : ""}`} data-testid={`vip-service-${s.id}`} onClick={() => setService(s.id)}>
                <span style={{ flex: 1 }}><h4 style={{ fontSize: 12.5, fontWeight: 800, margin: 0 }}>{s.name}</h4>
                  <span style={{ fontSize: 10, color: "#7B8B7F" }}>{s.desc}</span></span>
                <b style={{ fontSize: 12.5, color: "#0E7A4F" }}>dès {fmt(s.base)}</b>
              </div>
            ))}
            <div className="tp-sechead"><div><h2>Véhicule</h2></div></div>
            {VEHICLES.map((v) => (
              <div key={v.id} className={`tp-payrow ${vehicle === v.id ? "on" : ""}`} data-testid={`vip-vehicle-${v.id}`} onClick={() => setVehicle(v.id)}>
                <span className="tp-paylogo" style={{ background: vehicle === v.id ? "#0B3B27" : "#94A89B" }}>VIP</span>
                <span style={{ flex: 1 }}><h4 style={{ fontSize: 12.5, fontWeight: 800, margin: 0 }}>{v.name}</h4>
                  <span style={{ fontSize: 10, color: "#7B8B7F" }}>{v.desc}</span></span>
                <b style={{ fontSize: 12.5, color: "#0E7A4F" }}>{fmt(Math.round(svc.base * v.mult / 500) * 500)}</b>
              </div>
            ))}
            <PrimaryBtn testid="vip-service-continue" onClick={() => setStep("details")}>Continuer</PrimaryBtn>
          </>
        )}

        {step === "details" && (
          <>
            <div className="tp-summary">
              <span className="tp-paylogo" style={{ background: "#0B3B27", width: 30, height: 30, borderRadius: 9 }}>VIP</span>
              <span className="t"><h3>{svc.name} · {veh.name}</h3><p>{fmt(total)}</p></span>
            </div>
            <div className="tp-input" data-testid="vip-pickup">
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <MapPin size={15} color="#0E7A4F" />
                <span style={{ flex: 1 }}><label>Lieu de prise en charge (PADA)</label>
                  <select value={pickup.id} onChange={(e) => setPickup(TAXI_PLACES.find((p) => p.id === e.target.value))} data-testid="vip-pickup-select">
                    {TAXI_PLACES.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.commune}</option>)}
                  </select>
                </span>
              </span>
            </div>
            <div style={{ display: "flex", gap: 9 }}>
              <label className="tp-input" style={{ flex: 1 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Calendar size={14} color="#0E7A4F" />
                  <span style={{ flex: 1 }}><label>Date</label>
                    <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="vip-date" /></span>
                </span>
              </label>
              <label className="tp-input" style={{ flex: "0 0 38%" }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Clock size={14} color="#0E7A4F" />
                  <span style={{ flex: 1 }}><label>Heure</label>
                    <select value={hour} onChange={(e) => setHour(e.target.value)} data-testid="vip-hour">
                      {HOURS.map((h) => <option key={h}>{h}</option>)}
                    </select></span>
                </span>
              </label>
            </div>
            <label className="tp-input"><label>Instructions (optionnel)</label>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="vip-notes" placeholder="Ex : Passage à l'aéroport, 2 valises" />
            </label>
            <PrimaryBtn testid="vip-details-continue" onClick={() => setStep("payment")}>Continuer</PrimaryBtn>
          </>
        )}

        {step === "payment" && (
          <>
            <div className="tp-card" data-testid="vip-recap">
              <PriceRow label="Formule" value={svc.name} />
              <PriceRow label="Véhicule" value={veh.name} />
              <PriceRow label="Prise en charge" value={pickup.commune} />
              <PriceRow label="Date · Heure" value={`${dateLabel} · ${hour}`} />
              <PriceRow total label="Total" value={fmt(total)} />
            </div>
            <div className="tp-sechead"><div><h2>Mode de paiement</h2></div></div>
            <PaymentMethods value={payMethod} onChange={setPayMethod} />
            <PrimaryBtn testid="vip-pay-btn" disabled={paying} onClick={pay}>
              {paying ? "Paiement en cours…" : `Payer ${fmt(total)}`}
            </PrimaryBtn>
            <SecureNote />
          </>
        )}

        {step === "done" && (
          <>
            <div className="tp-confirm" data-testid="vip-confirmation">
              <span className="circ"><Check size={36} color="#fff" strokeWidth={3} /></span>
              <h2>Réservation confirmée !</h2>
              <p>Votre chauffeur privé vous attendra à l'heure convenue.</p>
            </div>
            <div className="tp-ref" data-testid="vip-booking-ref">
              <span><span className="lab">Référence réservation</span>{bookingRef}</span>
              <button className="tp-link" onClick={() => { navigator.clipboard?.writeText(bookingRef); toast.success("Référence copiée ✓"); }} aria-label="Copier"><Copy size={14} /></button>
            </div>
            <div className="tp-card" style={{ display: "flex", alignItems: "center", gap: 10 }} data-testid="vip-driver-card">
              <span className="tp-logo" style={{ background: "#0B3B27", width: 40, height: 40, borderRadius: "50%", fontSize: 13 }}>KA</span>
              <span style={{ flex: 1 }}>
                <b style={{ fontSize: 13 }}>{CHAUFFEUR.name} <BadgeCheck size={12} color="#0E7A4F" style={{ verticalAlign: -2 }} /></b>
                <div style={{ fontSize: 10.5, color: "#7B8B7F", display: "flex", alignItems: "center", gap: 3 }}>
                  <Star size={10} color="#F5A623" fill="#F5A623" /> {CHAUFFEUR.rating} ({CHAUFFEUR.rides} courses) · {CHAUFFEUR.car} · {CHAUFFEUR.plate}
                </div>
              </span>
              <button className="tp-back" data-testid="vip-call-driver" aria-label="Appeler" onClick={() => toast.info("Mise en relation avec votre chauffeur…")}><Phone size={14} /></button>
            </div>
            <div className="tp-card">
              <PriceRow label="Prise en charge" value={pickup.name} />
              <PriceRow label="Date · Heure" value={`${dateLabel} · ${hour}`} />
              <PriceRow total label="Montant payé" value={fmt(total)} />
            </div>
            <PrimaryBtn testid="vip-see-tickets" onClick={() => navigate("/mes-billets")}>Voir mes billets</PrimaryBtn>
            <div style={{ height: 8 }} />
            <PrimaryBtn ghost testid="vip-done-home" onClick={() => navigate("/search")}>Retour à l'accueil</PrimaryBtn>
          </>
        )}
      </div>
    </div>
  );
}
